<template>
  <div class="sh" :class="{ 'is-live': streaming }">
    <div v-if="!nodeId" class="sh__empty">Set OBS device in settings</div>
    <template v-else>
      <div class="sh__head">
        <span class="sh__pill" :class="streaming ? 'is-on' : 'is-off'">
          <i class="fas fa-circle"></i>
          {{ streaming ? 'LIVE' : 'OFF' }}
        </span>
        <strong class="sh__br">{{ bitrateDisplay }}</strong>
      </div>

      <div class="sh__stats">
        <div
          v-for="m in gauges"
          :key="m.key"
          class="sh__stat"
          :style="{ '--accent': m.color }"
        >
          <span class="sh__stat-val">{{ m.value }}</span>
          <span class="sh__stat-lbl">{{ m.label }}</span>
        </div>
      </div>

      <div class="sh__charts">
        <div v-for="s in chartRows" :key="s.key" class="sh__chart">
          <div class="sh__chart-head">
            <span>{{ s.label }}</span>
            <strong>{{ s.display }}</strong>
          </div>
          <svg class="sh__spark" viewBox="0 0 100 24" preserveAspectRatio="none">
            <defs>
              <linearGradient :id="'shg-' + s.key + '-' + uid" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" :stop-color="s.color" stop-opacity="0.3" />
                <stop offset="100%" :stop-color="s.color" stop-opacity="0" />
              </linearGradient>
            </defs>
            <polygon
              v-if="s.area"
              :fill="'url(#shg-' + s.key + '-' + uid + ')'"
              :points="s.area"
            />
            <polyline
              v-if="s.line"
              fill="none"
              :stroke="s.color"
              stroke-width="1.75"
              stroke-linejoin="round"
              stroke-linecap="round"
              vector-effect="non-scaling-stroke"
              :points="s.line"
            />
            <line
              v-else
              x1="0" y1="12" x2="100" y2="12"
              :stroke="s.color"
              stroke-width="1.25"
              stroke-opacity="0.25"
              vector-effect="non-scaling-stroke"
            />
          </svg>
        </div>
      </div>
    </template>

    <div v-if="error" class="sh__error">{{ error }}</div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

const POLL_MS = 2000
const HISTORY = 24

function healthColor(pct) {
  if (pct >= 75) return '#ef6e6e'
  if (pct >= 45) return '#ffbc34'
  return '#22c6ab'
}

function sparkPoints(values) {
  const nums = values.map((v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v)))
  const known = nums.filter((v) => v != null)
  if (known.length < 2) return { line: '', area: '', points: known }
  let min = Math.min(...known)
  let max = Math.max(...known)
  if (min === max) {
    min = Math.max(0, min - 1)
    max += 1
  }
  const pad = (max - min) * 0.12
  min -= pad
  max += pad
  const coords = nums.map((v, i) => {
    const x = nums.length === 1 ? 0 : (i / (nums.length - 1)) * 100
    const val = v == null ? known[known.length - 1] : v
    const y = 20 - ((val - min) / (max - min)) * 16
    return `${x.toFixed(2)},${y.toFixed(2)}`
  })
  const line = coords.join(' ')
  return { line, area: `0,24 ${line} 100,24`, points: known }
}

function fmtBitrate(bps) {
  const n = Number(bps)
  if (!Number.isFinite(n) || n <= 0) return '—'
  const kbps = n / 125
  if (kbps < 1000) return `${kbps.toFixed(0)} kbps`
  return `${(kbps / 1000).toFixed(2)} Mbps`
}

export default {
  props: {
    config: { type: Object, default: () => ({}) },
    widgetId: { type: String, default: '' },
    tabActive: { type: Boolean, default: true },
  },
  data() {
    return {
      streaming: false,
      fps: null,
      congestion: null,
      skippedPct: null,
      bitrateBps: null,
      history: [],
      lastBytes: null,
      lastBytesAt: 0,
      pending: '',
      error: '',
      pollTimer: null,
    }
  },
  computed: {
    uid() {
      return this.widgetId || 'sh'
    },
    nodeId() {
      return this.config.nodeId || ''
    },
    bitrateDisplay() {
      return fmtBitrate(this.bitrateBps)
    },
    gauges() {
      const fpsPct = this.fps == null ? 0 : Math.max(0, Math.min(100, (this.fps / 60) * 100))
      const congPct = this.congestion == null ? 0 : Math.max(0, Math.min(100, this.congestion * 100))
      const dropPct = this.skippedPct == null ? 0 : Math.max(0, Math.min(100, this.skippedPct))
      return [
        {
          key: 'fps',
          label: 'FPS',
          value: this.fps == null ? '—' : this.fps.toFixed(0),
          color: healthColor(this.fps != null && this.fps < 50 ? 55 : 100 - fpsPct),
        },
        {
          key: 'cong',
          label: 'Cong',
          value: this.congestion == null ? '—' : `${Math.round(congPct)}%`,
          color: healthColor(congPct),
        },
        {
          key: 'drop',
          label: 'Drop',
          value: this.skippedPct == null ? '—' : `${dropPct.toFixed(1)}%`,
          color: healthColor(Math.min(100, dropPct * 8)),
        },
      ]
    },
    chartRows() {
      const bitrate = sparkPoints(this.history.map((h) => h.bitrate))
      const fps = sparkPoints(this.history.map((h) => h.fps))
      return [
        {
          key: 'br',
          label: 'Bitrate',
          display: this.bitrateDisplay,
          color: '#4798e8',
          ...bitrate,
        },
        {
          key: 'fps',
          label: 'FPS',
          display: this.fps == null ? '—' : `${this.fps.toFixed(1)}`,
          color: '#22c6ab',
          ...fps,
        },
      ]
    },
  },
  watch: {
    tabActive(active) {
      if (!this.nodeId) return
      if (active) this.startPoll()
      else this.stopPoll()
    },
    nodeId: {
      immediate: true,
      handler(id) {
        this.stopPoll()
        this.resetState()
        if (id) {
          this.refresh()
          if (this.tabActive) this.startPoll()
        }
      },
    },
  },
  beforeDestroy() {
    this.stopPoll()
  },
  methods: {
    ...mapActions('mission', ['sendCommand']),
    resetState() {
      this.streaming = false
      this.fps = null
      this.congestion = null
      this.skippedPct = null
      this.bitrateBps = null
      this.history = []
      this.lastBytes = null
      this.lastBytesAt = 0
      this.error = ''
      this.pending = ''
    },
    startPoll() {
      this.stopPoll()
      this.pollTimer = setInterval(() => {
        if (this.nodeId && !document.hidden && !this.pending) this.refresh(true)
      }, POLL_MS)
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    parsePayload(res) {
      const msg = res && res.result
      if (!msg) return null
      if (msg.type === 'error' || msg.error) throw new Error(msg.error || 'Command failed')
      let payload = msg.payload
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload) } catch (e) { return null }
      }
      return payload
    },
    async cmd(action, payload = {}) {
      const res = await this.sendCommand({
        node_id: this.nodeId,
        adapter: 'obs',
        action,
        payload,
      })
      return this.parsePayload(res)
    },
    async refresh(soft = false) {
      if (!this.nodeId) return
      if (!soft) this.pending = 'refresh'
      try {
        const [stream, stats] = await Promise.all([
          this.cmd('GetStreamStatus'),
          this.cmd('GetStats'),
        ])
        this.streaming = !!(stream && stream.outputActive)
        this.congestion = stream && stream.outputCongestion != null
          ? Number(stream.outputCongestion)
          : null

        const skipped = Number(stream && stream.outputSkippedFrames) || 0
        const total = Number(stream && stream.outputTotalFrames) || 0
        this.skippedPct = total > 0
          ? (skipped / total) * 100
          : (stats && stats.renderSkippedFrames != null ? Number(stats.renderSkippedFrames) : null)

        this.fps = stats && stats.activeFps != null ? Number(stats.activeFps) : null

        const bytes = stream && stream.outputBytes != null ? Number(stream.outputBytes) : null
        const now = Date.now()
        if (bytes != null && this.lastBytes != null && this.lastBytesAt) {
          const dt = (now - this.lastBytesAt) / 1000
          if (dt > 0) this.bitrateBps = Math.max(0, (bytes - this.lastBytes) / dt)
        }
        if (bytes != null) {
          this.lastBytes = bytes
          this.lastBytesAt = now
        }

        this.history.push({ t: now, bitrate: this.bitrateBps, fps: this.fps })
        while (this.history.length > HISTORY) this.history.shift()
        if (!soft) this.error = ''
      } catch (e) {
        if (!soft) this.error = e.response?.data?.message || e.message || 'Error'
      } finally {
        if (!soft) this.pending = ''
      }
    },
  },
}
</script>

<style scoped>
.sh {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.35em;
  color: var(--ws-text);
  min-height: 0;
  overflow: hidden;
  padding: 0.4em 0.5em;
  box-sizing: border-box;
  background: var(--ws-widget-bg);
  container-type: size;
}
.sh.is-live {
  background:
    radial-gradient(ellipse at 100% 0%, rgba(239, 110, 110, 0.14), transparent 42%),
    var(--ws-widget-bg);
}
.sh__empty,
.sh__error {
  font-size: 0.78em;
  color: var(--ws-text-muted, #6c757d);
}
.sh__error { color: var(--ws-error, #ef6e6e); }

.sh__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.45em;
  flex-shrink: 0;
  min-width: 0;
}
.sh__pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  padding: 0.15em 0.5em;
  border-radius: 999px;
  font-size: 0.62em;
  font-weight: 800;
  letter-spacing: 0.05em;
  flex-shrink: 0;
}
.sh__pill i { font-size: 0.5em; }
.sh__pill.is-on {
  background: rgba(239, 110, 110, 0.18);
  color: #ff8e8e;
}
.sh__pill.is-off {
  background: var(--ws-ctrl-bg, rgba(128, 128, 128, 0.15));
  color: var(--ws-text-muted);
}
.sh__br {
  font-size: 0.95em;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.sh__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.28em;
  flex-shrink: 0;
}
.sh__stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.05em;
  min-width: 0;
  padding: 0.22em 0.15em;
  border-radius: 5px;
  background: var(--ws-ctrl-bg, rgba(128, 128, 128, 0.1));
  border-left: 2px solid var(--accent, #22c6ab);
}
.sh__stat-val {
  font-size: 0.82em;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}
.sh__stat-lbl {
  font-size: 0.52em;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ws-text-muted);
  white-space: nowrap;
}

.sh__charts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.3em;
  flex: 1;
  min-height: 0;
  align-content: stretch;
  align-items: stretch;
}
.sh__chart {
  display: flex;
  flex-direction: column;
  gap: 0.12em;
  min-width: 0;
  min-height: 0;
}
.sh__chart-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.35em;
  font-size: 0.62em;
  line-height: 1.1;
  min-width: 0;
  flex-shrink: 0;
}
.sh__chart-head span {
  color: var(--ws-text-muted);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  flex-shrink: 0;
}
.sh__chart-head strong {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.sh__spark {
  width: 100%;
  flex: 1;
  min-height: 28px;
  max-height: none;
  display: block;
  border-radius: 3px;
  background: var(--ws-ctrl-bg, rgba(128, 128, 128, 0.08));
}

@container (max-width: 220px) {
  .sh__charts {
    grid-template-columns: 1fr;
  }
  .sh__br {
    font-size: 0.82em;
  }
}

@container (max-height: 150px) {
  .sh__charts {
    display: none;
  }
  .sh {
    gap: 0.25em;
    padding: 0.35em 0.45em;
  }
  .sh__stat-val {
    font-size: 0.75em;
  }
}

@container (max-height: 110px) {
  .sh__stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.2em;
  }
  .sh__stat {
    flex-direction: row;
    justify-content: center;
    gap: 0.25em;
    padding: 0.15em 0.2em;
    border-left-width: 0;
    border-top: 2px solid var(--accent, #22c6ab);
  }
  .sh__stat-lbl::after {
    content: ':';
  }
}
</style>
